import { useActiveStation } from '@app/core/stations/hooks/useActiveStation';
import { useFeatureValue } from '@growthbook/growthbook-react';
import { useCallback } from 'react';
import { useStreamSelection } from './useStreamSelection';

const DEFAULT_MAX_SELECTION_DURATION_HOURS = 3; // 3 hours

type SelectionType = 'start' | 'end';

function useCanSelectionWithDurationLimit(type: SelectionType) {
  const station = useActiveStation();
  const maxDurationHours = useFeatureValue(
    'fragments__max_duration_allowed',
    DEFAULT_MAX_SELECTION_DURATION_HOURS,
  );
  const maxDurationMs = maxDurationHours * 60 * 60 * 1000;
  const { selection, canStartSelection, canEndSelection } = useStreamSelection();

  return useCallback(
    (ts: number) => {
      // First check if selection can be started/ended using base hook
      const baseCheck = type === 'start' ? canStartSelection(ts) : canEndSelection(ts);

      if (!baseCheck) {
        return false;
      }

      // Get current selection state from hook
      const { tsA, tsB } = selection;

      // Check duration against the opposite boundary
      // When starting, check against tsB (if it exists)
      // When ending, check against tsA (if it exists)
      const oppositeTimestamp = type === 'start' ? tsB : tsA;

      if (oppositeTimestamp !== null) {
        const duration = Math.abs(ts - oppositeTimestamp);
        if (duration > maxDurationMs) {
          return false;
        }
      }

      return true;
    },
    [maxDurationMs, type, canStartSelection, canEndSelection, selection],
  );
}

export function useCanStartSelectionWithDurationLimit() {
  return useCanSelectionWithDurationLimit('start');
}

export function useCanEndSelectionWithDurationLimit() {
  return useCanSelectionWithDurationLimit('end');
}
